import { Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { CrmFieldType, type CrmObject, type CrmRowFieldsFragment, CrmSortDirection } from "../../generated/graphql";
import { useCrmLabels } from "../../hooks/useCrmLabels";
import { CRM_COLORS, crmRecordPath, formatCrmValue } from "../../lib/crm";
import { CrmRecordProvider } from "./CrmRecordContext";
import { CrmValueView } from "./CrmValueView";
import { DeletedInSalesforceBadge } from "./DeletedInSalesforceBadge";

export type CrmSort = { key: string; direction: CrmSortDirection };

type CrmDataTableProps = {
  object: CrmObject;
  columns: { key: string; type: CrmFieldType }[];
  rows: CrmRowFieldsFragment[];
  sort?: CrmSort;
  onSort?: (key: string) => void;
  // A compact table inside a related list card.
  dense?: boolean;
  maxHeight?: string;
};

const NUMERIC_TYPES = new Set([CrmFieldType.Number, CrmFieldType.Currency]);

export const CrmDataTable = ({ object, columns, rows, sort, onSort, dense = false, maxHeight }: CrmDataTableProps) => {
  const labels = useCrmLabels();
  const cellSx = { fontSize: "13px", py: dense ? "6px" : "8px", px: "12px", color: CRM_COLORS.text };

  return (
    <TableContainer sx={{ maxHeight, backgroundColor: CRM_COLORS.cardBackground }}>
      <Table size="small" stickyHeader aria-label={labels.object(object, true)}>
        <TableHead>
          <TableRow>
            {columns.map(column => {
              const active = sort?.key === column.key;
              const direction = active && sort?.direction === CrmSortDirection.Desc ? "desc" : "asc";
              return (
                <TableCell
                  key={column.key}
                  align={NUMERIC_TYPES.has(column.type) ? "right" : "left"}
                  sortDirection={active ? direction : false}
                  sx={{
                    ...cellSx,
                    backgroundColor: CRM_COLORS.tableHeader,
                    color: CRM_COLORS.textWeak,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    textTransform: dense ? "none" : undefined,
                  }}
                >
                  {onSort ? (
                    <TableSortLabel active={active} direction={direction} onClick={() => onSort(column.key)}>
                      {labels.field(object, column.key)}
                    </TableSortLabel>
                  ) : (
                    labels.field(object, column.key)
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow hover key={row.salesforceId} sx={{ "&:hover": { backgroundColor: CRM_COLORS.rowHover } }}>
              {columns.map((column, index) => {
                const value = row.values.find(candidate => candidate.key === column.key);
                return (
                  <TableCell
                    key={column.key}
                    align={NUMERIC_TYPES.has(column.type) ? "right" : "left"}
                    sx={{
                      ...cellSx,
                      maxWidth: 320,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {/* The first column opens the row's record, or the record it references. */}
                    {index === 0 && value?.type !== CrmFieldType.Reference ? (
                      <Link
                        component={RouterLink}
                        to={crmRecordPath(object, row.salesforceId)}
                        sx={{
                          color: CRM_COLORS.link,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {(value && formatCrmValue(value.type, value.value)) || row.title}
                      </Link>
                    ) : (
                      value && (
                        <CrmRecordProvider object={object} salesforceId={row.salesforceId}>
                          <CrmValueView value={value} />
                        </CrmRecordProvider>
                      )
                    )}
                    {index === 0 && row.deletedInSalesforceAt && (
                      <DeletedInSalesforceBadge deletedAt={row.deletedInSalesforceAt} />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
